import React from 'react';

import {toInteger} from 'lodash';
import {useSelector} from 'react-redux';
import {withRouter} from 'react-router';

import {PendingAcceptanceInfoBar} from 'app/acceptance/components/PendingAcceptanceInfoBar';
import {PageWithRightSidebar} from 'app/components/general/DetailPage/PageWithRightSidebar';
import {
  type GenericDocumentPropsForDetailPage,
  RenderWhenDocumentIsGatheredDetailPage,
} from 'app/components/general/DetailPage/RenderWhenDocumentIsGathered';
import {StickyComments} from 'app/components/general/DetailPage/RightSidebar/StickyDocumentCommentsSidebar';
import {GenericDocumentActionButtons} from 'app/components/general/GenericDocumentActionButtons';
import VersionInfoBar from 'app/components/general/VersionInfoBar';
import {showNoApprovedVersionsAlertBoxIfNotApproved} from 'app/general/structures/document-alerts/NoApprovedVersionsAlertBox';
import {MainContentContainer} from 'app/general/structures/MainContentContainer';
import {VideoManagerProvider} from 'app/general/VideoManagerProvider';
import {
  usePolicyByHashid,
  usePolicyVersionById,
  usePolicyVersions,
} from 'app/policies/api';
import {DocumentPresenceMembers} from 'app/presence/components/DocumentPresenceMembers';
import {toggleProcedureAdvancedMode} from 'app/procedures/actions';
import {ProcedureHeaderOptionsDropdown} from 'app/procedures/components/ProcedureHeaderOptionsDropdown';
import {ReviewReminderWarning} from 'app/review/components/ReviewReminderWarning';
import {SignoffForm} from 'app/signoffs/components/SignoffForm';
import {SignoffRequiredInfoBar} from 'app/signoffs/components/SignoffRequiredInfoBar';
import {
  allRelatedMatch,
  modelIdMatch,
  useModelChangeSubscription,
} from 'app/subscriptions';
import {getPolicyName} from 'app/utils/fallbacks';
import {gt} from 'app/utils/i18n';
import {whenVisible} from 'app/utils/page-visibility';
import {useAsyncActions} from 'app/utils/redux';
import {isLastVersion} from 'app/version/utils';

import {PolicyDiffer} from './PolicyDiffer';
import {PolicyPageSidebar} from './PolicyPageSidebar';

import {getContentFromPolicy} from '../PolicyEditPage/utils';
import {ViewPolicyContent} from '../ViewPolicyContent';
import {ViewPolicyMetadata} from '../ViewPolicyMetadata';

import type {
  DetailPageBaseProps,
  DetailPageRouterProps,
} from 'app/components/general/DetailPage/types';
import type {ModelChangeMessageType} from 'app/subscriptions';
import type {DetailPolicySerializedType} from 'sweet-types';

export function PolicyDetailPage({
  route,
}: DetailPageBaseProps<DetailPolicySerializedType>) {
  return (
    <RenderWhenDocumentIsGatheredDetailPage
      route={route}
      useHashidHook={usePolicyByHashid}
      useSelectedVersionHook={usePolicyVersionById}
      useVersionsHook={usePolicyVersions}
      DetailPage={PolicyDetailPageWithRouter}
    />
  );
}

const rightSidebarWidgets = [StickyComments];
export const BasePolicyDetailPage = ({
  route,
  object: policy,
  version,
  versions,
  refetch,
  refetchVersion,
  location,
  params,
}: GenericDocumentPropsForDetailPage<DetailPolicySerializedType> &
  DetailPageRouterProps) => {
  const deletingDocument = useSelector(
    (state) => state.toasts.isDeletingDocument
  );

  const CurrentLeftSidebar = route.props?.CurrentLeftSidebar;
  const CurrentRightSidebar = route.props?.CurrentRightSidebar;

  const advancedMode = useSelector((state) =>
    state.proceduresPage.get('procedureAdvancedMode')
  );

  const refetchData = whenVisible(() => {
    refetch();
    refetchVersion();
  });

  const actions = useAsyncActions({
    toggleProcedureAdvancedMode,
  });

  const content = getContentFromPolicy(version ?? policy);
  useModelChangeSubscription(
    (message: ModelChangeMessageType, {runWhenAny}) => {
      if (deletingDocument) {
        return;
      }
      const expectedRelated = {
        content_type: 'policy',
        id: policy.id,
      };
      runWhenAny(
        message,
        [
          modelIdMatch('policy', policy.id),
          allRelatedMatch('policyversion', expectedRelated),
          allRelatedMatch('review', expectedRelated),
          allRelatedMatch('comment', expectedRelated),
        ],
        refetchData
      );
    },
    [deletingDocument]
  );

  if (content == null) {
    return null;
  }

  const actionButtonsProps = {
    contentType: 'policy' as const,
    canChange: policy.can_change,
    refreshData: refetchData,
    additionalContent: (
      <ProcedureHeaderOptionsDropdown
        advancedModeEnabled={advancedMode}
        toggleAdvancedMode={actions.toggleProcedureAdvancedMode}
      />
    ),
  };

  return (
    <PolicyDiffer
      location={location}
      content={content}
      versions={versions ?? []}
      versionAvailable={version != null}
    >
      {(contentWithDiff, showingDiff, toggleDiff) => (
        <PageWithRightSidebar
          context={'view'}
          useHashidHook={usePolicyByHashid}
          useSelectedVersionHook={usePolicyVersionById}
          breadcrumbProps={{
            docName: getPolicyName(policy),
            version: version,
          }}
          actionArea={
            version != null ? (
              <GenericDocumentActionButtons
                version={version}
                isLastVersion={isLastVersion(version, versions)}
                pageContext="view-version"
                document={policy}
                {...actionButtonsProps}
              />
            ) : (
              <GenericDocumentActionButtons
                pageContext="view-published"
                document={policy}
                {...actionButtonsProps}
              />
            )
          }
          LeftSidebar={PolicyPageSidebar}
          CurrentLeftSidebar={CurrentLeftSidebar}
          CurrentRightSidebar={CurrentRightSidebar}
          rightSidebarPanels={rightSidebarWidgets}
          versionbar={
            <>
              {version != null ? (
                <VersionInfoBar
                  objectTypeName={gt('policy')}
                  version={version}
                  showingDiff={showingDiff}
                  toggleDiff={toggleDiff}
                />
              ) : null}
              {!(CurrentLeftSidebar?.sidebarName === 'showAccept') && (
                <PendingAcceptanceInfoBar
                  document={policy}
                  location={location}
                  openedVersion={version}
                />
              )}
              {version == null ? (
                <SignoffRequiredInfoBar
                  contentType="policy"
                  objectId={policy.id}
                  viewChangesURL={policy.current_version.html_url}
                />
              ) : null}
            </>
          }
        >
          <DocumentPresenceMembers
            contentType="policy"
            objectId={policy.id}
            pageContext="view"
          />
          <VideoManagerProvider>
            <ViewPolicyMetadata
              policyContent={contentWithDiff}
              policy={policy}
              versionId={toInteger(
                params.versionid || policy.current_version.id
              )}
              advancedModeEnabled={advancedMode}
              warningComponent={
                <div>
                  {showNoApprovedVersionsAlertBoxIfNotApproved(
                    'policy',
                    policy.approved_at
                  )}
                  <ReviewReminderWarning
                    contentType="policy"
                    versionId={policy.current_version.id}
                    versionContentType="policyversion"
                    canReview={policy.can_edit}
                    onReview={refetch}
                    reviewDate={policy.next_review_at}
                  />
                </div>
              }
            />
            <MainContentContainer>
              <ViewPolicyContent
                editable={false}
                content={contentWithDiff}
                versionId={toInteger(
                  params.versionid || policy.current_version.id
                )}
                objectId={policy.id}
              />
              {version == null ? (
                <SignoffForm
                  objectId={policy.id}
                  contentType="policy"
                  versionId={policy.current_version.id}
                  versionContentType="policyversion"
                  objectName={policy.name}
                />
              ) : null}
            </MainContentContainer>
          </VideoManagerProvider>
        </PageWithRightSidebar>
      )}
    </PolicyDiffer>
  );
};

const PolicyDetailPageWithRouter =
  withRouter<GenericDocumentPropsForDetailPage<DetailPolicySerializedType>>(
    BasePolicyDetailPage
  );
